import { getCsrfToken } from 'utils/csrf';

// Minimal fetch helpers for the Registry Admin endpoints.

export class ReauthRequiredError extends Error {
  constructor(reauthUrl) {
    super('Google re-authentication required');
    this.name = 'ReauthRequiredError';
    this.reauthUrl = reauthUrl;
  }
}

// Most write helpers below throw the server's JSON error body as the message; this pulls a
// readable message back out of it (and passes a plain-text message through).
export const errorText = (e, fallback) => {
  try {
    const parsed = JSON.parse(e.message);
    return [].concat(parsed.non_field_errors || parsed.detail || parsed.error || fallback).join(' ');
  } catch {
    return e.message || fallback;
  }
};

async function throwOnError(response, fallbackMessage) {
  const body = await response.json().catch(() => ({}));
  if (response.status === 401 && body.reauth_url) throw new ReauthRequiredError(body.reauth_url);
  throw new Error(body.detail || body.error || fallbackMessage);
}

export async function fetchList(apiEndpoint) {
  const response = await fetch(apiEndpoint, { credentials: 'include' });
  if (!response.ok) {
    await throwOnError(response, `Failed to fetch ${apiEndpoint} (${response.status})`);
  }
  return await response.json();
}

export async function fetchRecord(apiEndpoint, id) {
  const response = await fetch(`${apiEndpoint}/${id}`, { credentials: 'include' });
  if (!response.ok) {
    await throwOnError(response, `Failed to fetch record (${response.status})`);
  }
  return await response.json();
}

export async function createRecord(apiEndpoint, data) {
  const response = await fetch(apiEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': getCsrfToken(),
    },
    body: JSON.stringify(data),
    credentials: 'include',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(JSON.stringify(errorData) || 'Failed to create');
  }
  return await response.json();
}

export async function updateRecord(apiEndpoint, id, data) {
  const response = await fetch(`${apiEndpoint}/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': getCsrfToken(),
    },
    body: JSON.stringify(data),
    credentials: 'include',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(JSON.stringify(errorData) || 'Failed to update');
  }
  return await response.json();
}

export async function deleteRecord(apiEndpoint, id) {
  const response = await fetch(`${apiEndpoint}/${id}`, {
    method: 'DELETE',
    headers: { 'X-CSRFToken': getCsrfToken() },
    credentials: 'include',
  });
  if (!response.ok) {
    await throwOnError(response, `Failed to delete record (${response.status})`);
  }
}

export async function patchRecord(apiEndpoint, id, data) {
  const response = await fetch(`${apiEndpoint}/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': getCsrfToken(),
    },
    body: JSON.stringify(data),
    credentials: 'include',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(JSON.stringify(errorData) || 'Failed to update');
  }
  return await response.json();
}

export async function postAction(apiEndpoint, id, action) {
  const response = await fetch(`${apiEndpoint}/${id}/${action}`, {
    method: 'POST',
    headers: { 'X-CSRFToken': getCsrfToken() },
    credentials: 'include',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(JSON.stringify(errorData) || `Failed to ${action}`);
  }
  return await response.json();
}

// -- Experiment file uploads (two-step signed URL flow) --------------------

export async function startFileUpload(payload) {
  const response = await fetch('/registry/files/start', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': getCsrfToken(),
    },
    body: JSON.stringify(payload),
    credentials: 'include',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to start upload');
  }
  return await response.json();
}

export async function uploadFileToGCS(uploadUrl, file, uploadHeaders = {}) {
  // uploadHeaders are the ones the signed URL was signed with (e.g. the size-limit
  // header); GCS 403s the PUT if they're missing or altered, so send them verbatim.
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream', ...uploadHeaders },
    body: file,
  });
  if (!response.ok) {
    throw new Error('Upload to storage failed');
  }
}

export async function confirmFileUpload(fileId, body = {}) {
  const response = await fetch(`/registry/files/${fileId}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCsrfToken() },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to confirm upload');
  }
  return await response.json();
}

export async function getFileDownloadUrl(fileId) {
  const response = await fetch(`/registry/files/${fileId}`, { credentials: 'include' });
  if (!response.ok) {
    throw new Error('Failed to get download link');
  }
  return await response.json();
}

export async function updateFileCategory(fileId, documentCategory) {
  const response = await fetch(`/registry/files/${fileId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': getCsrfToken(),
    },
    body: JSON.stringify({ document_category: documentCategory }),
    credentials: 'include',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to update document type');
  }
  return await response.json();
}

export async function deleteExperimentFile(fileId) {
  const response = await fetch(`/registry/files/${fileId}`, {
    method: 'DELETE',
    headers: { 'X-CSRFToken': getCsrfToken() },
    credentials: 'include',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to delete file');
  }
}
